/**
 * PortalBookingSettings - Booking rules settings for Customer Portal
 */
import React, { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle
} from '@/components/ui/alert-dialog';
import { Calendar, ArrowLeft, Save, Loader2, Clock, Users, Key, Globe, Zap, Snowflake, Dumbbell, MessageCircle, Shield, Eye, EyeOff, BellRing, MapPin, Cake } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Service icons mapping
const getServiceIcon = (serviceName) => {
  const name = serviceName.toLowerCase();
  if (name.includes('ems')) return Zap;
  if (name.includes('kryo') || name.includes('cryo')) return Snowflake;
  if (name.includes('beratung') || name.includes('consult')) return MessageCircle;
  return Dumbbell;
};

const PortalBookingSettings = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [services, setServices] = useState([]);
  const [showSecretKey, setShowSecretKey] = useState(false);
  const [settings, setSettings] = useState({
    min_booking_hours: 2,
    max_booking_days: 30,
    cancellation_hours: 24,
    reschedule_hours: 6,
    show_trainer_selection: true,
    show_resource_selection: false,
    require_login_to_book: true,
    enable_registration: true,
    emergency_contact_required: false,
    address_required: false,
    birthdate_required: true,
    public_booking_services: [], // List of service IDs enabled for public booking
    // reCAPTCHA settings
    recaptcha_enabled: false,
    recaptcha_site_key: '',
    recaptcha_secret_key: ''
  });
  const [reminderOpen, setReminderOpen] = useState(false);
  const [reminderCount, setReminderCount] = useState(null);
  const [reminderSending, setReminderSending] = useState(false);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const token = localStorage.getItem('token');
      const [settingsRes, servicesRes] = await Promise.all([
        axios.get(`${API}/portal/settings`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/services`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      
      setSettings(prev => ({ ...prev, ...settingsRes.data }));
      setServices(servicesRes.data.filter(s => s.is_active !== false));
    } catch (error) {
      console.error('Load settings error:', error);
    } finally {
      setLoading(false);
    }
  };

  const openReminderDialog = async () => {
    setReminderCount(null);
    setReminderOpen(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/portal/settings/emergency-reminder/count`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setReminderCount(res.data?.count ?? 0);
    } catch (e) {
      setReminderCount(0);
    }
  };

  const sendReminders = async () => {
    setReminderSending(true);
    try {
      const token = localStorage.getItem('token');
      const res = await axios.post(`${API}/portal/settings/emergency-reminder`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const d = res.data || {};
      toast.success(`Erinnerung gesendet: E-Mail an ${d.emails_sent || 0}, Push an ${d.push_sent || 0} Kunden`);
      setReminderOpen(false);
    } catch (error) {
      toast.error('Versand fehlgeschlagen');
    } finally {
      setReminderSending(false);
    }
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const token = localStorage.getItem('token');
      await axios.put(`${API}/portal/settings`, settings, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Einstellungen gespeichert');
    } catch (error) {
      toast.error('Speichern fehlgeschlagen');
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="p-8 flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-green-500" />
      </div>
    );
  }

  return (
    <div className="p-8 max-w-4xl">
      <Button variant="ghost" className="mb-4 -ml-2" onClick={() => navigate('/portal-settings')}>
        <ArrowLeft className="h-4 w-4 mr-2" /> Zurück
      </Button>
      
      <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2 mb-6">
        <Calendar className="h-6 w-6 text-orange-500" /> Buchungsregeln
      </h1>

      <div className="space-y-6">
        {/* Zeiten */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Clock className="h-5 w-5" /> Zeitregeln
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <Label>Mindestvorlauf (Stunden)</Label>
                <Input 
                  type="number" 
                  value={settings.min_booking_hours} 
                  onChange={(e) => setSettings({...settings, min_booking_hours: parseInt(e.target.value) || 0})} 
                  min="0"
                  className="mt-1" 
                />
                <p className="text-xs text-gray-500 mt-1">Wie viele Stunden vor dem Termin kann man noch buchen?</p>
              </div>
              <div>
                <Label>Max. Tage im Voraus</Label>
                <Input 
                  type="number" 
                  value={settings.max_booking_days} 
                  onChange={(e) => setSettings({...settings, max_booking_days: parseInt(e.target.value) || 0})} 
                  min="1"
                  className="mt-1" 
                />
                <p className="text-xs text-gray-500 mt-1">Wie weit im Voraus können Termine gebucht werden?</p>
              </div>
              <div>
                <Label>Stornierungsfrist (Stunden)</Label>
                <Input 
                  type="number" 
                  value={settings.cancellation_hours} 
                  onChange={(e) => setSettings({...settings, cancellation_hours: parseInt(e.target.value) || 0})} 
                  min="0"
                  className="mt-1" 
                />
                <p className="text-xs text-gray-500 mt-1">Bis wie viele Stunden vorher kostenlos stornierbar?</p>
              </div>
              <div>
                <Label>Verschiebe-Frist (Stunden)</Label>
                <Input 
                  type="number" 
                  value={settings.reschedule_hours} 
                  onChange={(e) => setSettings({...settings, reschedule_hours: parseInt(e.target.value) || 0})} 
                  min="0"
                  className="mt-1" 
                  data-testid="reschedule-hours-input"
                />
                <p className="text-xs text-gray-500 mt-1">Bis wie viele Stunden vorher darf ein Termin verschoben werden? (Portal &amp; App)</p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Optionen */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Users className="h-5 w-5" /> Anzeigeoptionen
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Trainer-Auswahl anzeigen</Label>
                <p className="text-sm text-gray-500">Kunden können einen spezifischen Trainer wählen</p>
              </div>
              <Switch 
                checked={settings.show_trainer_selection} 
                onCheckedChange={(checked) => setSettings({...settings, show_trainer_selection: checked})} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Ressourcen-Auswahl anzeigen</Label>
                <p className="text-sm text-gray-500">Kunden können Räume/Geräte wählen</p>
              </div>
              <Switch 
                checked={settings.show_resource_selection} 
                onCheckedChange={(checked) => setSettings({...settings, show_resource_selection: checked})} 
              />
            </div>
          </CardContent>
        </Card>

        {/* Login */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Key className="h-5 w-5" /> Anmeldung
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Login erforderlich zum Buchen</Label>
                <p className="text-sm text-gray-500">Kunden müssen sich anmelden, um zu buchen</p>
              </div>
              <Switch 
                checked={settings.require_login_to_book} 
                onCheckedChange={(checked) => setSettings({...settings, require_login_to_book: checked})} 
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Notfallkontakt verpflichtend</Label>
                <p className="text-sm text-gray-500">Kunden müssen einen Notfallkontakt angeben (Portal &amp; App*). *App erst nach nächstem Build.</p>
              </div>
              <Switch
                data-testid="emergency-contact-required-switch"
                checked={settings.emergency_contact_required}
                onCheckedChange={(checked) => setSettings({...settings, emergency_contact_required: checked})}
              />
            </div>
            {/* Automatische Erinnerung + manueller Sofortversand */}
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-lg space-y-2">
              <div className="flex items-start gap-2">
                <BellRing className="h-4 w-4 text-amber-600 mt-0.5 shrink-0" />
                <p className="text-sm text-amber-800">
                  Kunden ohne Notfallkontakt werden <strong>automatisch täglich</strong> erinnert
                  (E-Mail &amp; Push, max. 1× pro 14 Tage). Sobald ein Kunde den Kontakt einträgt,
                  hört die Erinnerung automatisch auf. Du kannst jetzt zusätzlich sofort erinnern:
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                className="border-amber-300 text-amber-800 hover:bg-amber-100"
                onClick={openReminderDialog}
                data-testid="send-emergency-reminder-btn"
              >
                <BellRing className="h-4 w-4 mr-2" /> Jetzt Erinnerung senden
              </Button>
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="flex items-center gap-2"><Cake className="h-4 w-4 text-gray-500" /> Geburtsdatum verpflichtend</Label>
                <p className="text-sm text-gray-500">Kunden müssen ihr Geburtsdatum angeben (Portal &amp; App*).</p>
              </div>
              <Switch
                data-testid="birthdate-required-switch"
                checked={settings.birthdate_required}
                onCheckedChange={(checked) => setSettings({...settings, birthdate_required: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label className="flex items-center gap-2"><MapPin className="h-4 w-4 text-gray-500" /> Adresse verpflichtend</Label>
                <p className="text-sm text-gray-500">Kunden müssen Strasse, PLZ und Ort angeben (Portal &amp; App*).</p>
              </div>
              <Switch
                data-testid="address-required-switch"
                checked={settings.address_required}
                onCheckedChange={(checked) => setSettings({...settings, address_required: checked})}
              />
            </div>
            <div className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
              <div>
                <Label>Registrierung aktiviert</Label>
                <p className="text-sm text-gray-500">Neue Kunden können sich selbst registrieren</p>
              </div>
              <Switch 
                checked={settings.enable_registration} 
                onCheckedChange={(checked) => setSettings({...settings, enable_registration: checked})} 
              />
            </div>
          </CardContent>
        </Card>

        {/* reCAPTCHA Einstellungen */}
        <Card className="border-2 border-blue-200">
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Shield className="h-5 w-5 text-blue-600" /> Spam-Schutz (reCAPTCHA)
            </CardTitle>
            <CardDescription>
              Schützen Sie die öffentliche Buchungsseite vor Spam und Bots mit Google reCAPTCHA v2.
              <a 
                href="https://www.google.com/recaptcha/admin/create" 
                target="_blank" 
                rel="noopener noreferrer"
                className="text-blue-600 hover:underline ml-1"
              >
                reCAPTCHA Keys erstellen →
              </a>
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="flex items-center justify-between p-4 bg-blue-50 rounded-lg">
              <div>
                <Label>reCAPTCHA aktivieren</Label>
                <p className="text-sm text-gray-500">"Ich bin kein Roboter"-Checkbox auf der Buchungsseite anzeigen</p>
              </div>
              <Switch 
                checked={settings.recaptcha_enabled} 
                onCheckedChange={(checked) => setSettings({...settings, recaptcha_enabled: checked})} 
              />
            </div>
            
            {settings.recaptcha_enabled && (
              <div className="space-y-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <Label>Site Key (öffentlich)</Label>
                  <Input 
                    type="text" 
                    value={settings.recaptcha_site_key || ''} 
                    onChange={(e) => setSettings({...settings, recaptcha_site_key: e.target.value})} 
                    placeholder="6Lc..."
                    className="mt-1 font-mono text-sm" 
                  />
                  <p className="text-xs text-gray-500 mt-1">Der Site Key wird im Frontend verwendet</p>
                </div>
                <div>
                  <Label>Secret Key (geheim)</Label>
                  <div className="relative mt-1">
                    <Input 
                      type={showSecretKey ? "text" : "password"} 
                      value={settings.recaptcha_secret_key || ''} 
                      onChange={(e) => setSettings({...settings, recaptcha_secret_key: e.target.value})} 
                      placeholder="6Lc..."
                      className="font-mono text-sm pr-10" 
                    />
                    <button
                      type="button"
                      onClick={() => setShowSecretKey(!showSecretKey)}
                      className="absolute right-2 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700"
                    >
                      {showSecretKey ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
                    </button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">Der Secret Key wird zur Verifizierung im Backend verwendet</p>
                </div>
                
                {(!settings.recaptcha_site_key || !settings.recaptcha_secret_key) && (
                  <div className="p-3 bg-yellow-100 border border-yellow-300 rounded-lg text-sm text-yellow-800">
                    ⚠️ Bitte beide Keys eintragen, damit reCAPTCHA funktioniert.
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        <div className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => navigate('/portal-settings')}>Abbrechen</Button>
          <Button onClick={handleSave} disabled={saving}>
            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
            Speichern
          </Button>
        </div>
      </div>

      <AlertDialog open={reminderOpen} onOpenChange={setReminderOpen}>
        <AlertDialogContent data-testid="emergency-reminder-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Notfallkontakt-Erinnerung senden?</AlertDialogTitle>
            <AlertDialogDescription>
              {reminderCount === null ? (
                <span className="flex items-center gap-2"><Loader2 className="h-4 w-4 animate-spin" /> Prüfe, wie viele Kunden betroffen sind…</span>
              ) : reminderCount === 0 ? (
                <>Aktuell haben <strong>alle Kunden</strong> einen Notfallkontakt hinterlegt. Es wird niemand benachrichtigt.</>
              ) : (
                <><strong>{reminderCount}</strong> Kunden haben noch keinen Notfallkontakt hinterlegt und erhalten jetzt eine Erinnerung per E-Mail und Push. Bereits eingetragene Kontakte werden übersprungen.</>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="emergency-reminder-cancel">Abbrechen</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => { e.preventDefault(); sendReminders(); }}
              disabled={reminderSending || reminderCount === 0}
              data-testid="emergency-reminder-confirm"
            >
              {reminderSending ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" /> Sende…</> : 'Jetzt senden'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default PortalBookingSettings;
